import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getMyInbox } from '@/services/alerts';
import type { IncidentTriage, TriageStatus } from '@/types';

// The inbox is triage-centric: my_inbox() returns the caller's personalized set
// (floor + subscription matches + assigned/claimed) server-side. On any triage
// realtime event we refetch (debounced) so we never re-implement the match logic
// client-side. Incident volume is low, so this is cheap and always correct.
export function useInbox(statuses?: TriageStatus[], onNewFloor?: (t: IncidentTriage) => void) {
  const [items, setItems] = useState<IncidentTriage[]>([]);
  const [loading, setLoading] = useState(true);
  const knownIds = useRef<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refetch = useCallback(async () => {
    try {
      const rows = await getMyInbox(statuses);
      // fire the new-floor callback for freshly-appeared floor incidents
      if (onNewFloor) {
        for (const r of rows) {
          if (r.is_floor && !knownIds.current.has(r.incident_id)) onNewFloor(r);
        }
      }
      knownIds.current = new Set(rows.map((r) => r.incident_id));
      setItems(rows);
    } catch (e) {
      console.error('[useInbox] refetch failed', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(statuses)]);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void refetch(), 350);
  }, [refetch]);

  useEffect(() => {
    void refetch();
    const ch = supabase
      .channel('alerts-inbox')
      .on('postgres_changes', { event: '*', schema: 'alerts', table: 'incident_triage' }, schedule)
      .subscribe();
    // safety net if the socket drops
    const poll = setInterval(() => void refetch(), 60_000);
    return () => { supabase.removeChannel(ch); clearInterval(poll); if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, schedule]);

  return { items, loading, refetch };
}
